import * as React from "react";
import * as Toast from "@radix-ui/react-toast";
import { XIcon } from "@phosphor-icons/react";
import { cn } from "./cn";

export type ToastTone = "success" | "error";

export type ToastContextValue = {
  /** Short success notice (auto-hide via provider `duration`). */
  success: (message: string) => void;
  /** Short error notice — prefer this when a blocking ErrorAlert dialog is too heavy. */
  error: (message: string) => void;
};

const ToastContext = React.createContext<ToastContextValue | null>(null);

type ToastItem = { id: number; message: string; tone: ToastTone };

export type ToastProviderLabels = {
  regionLabel?: string;
  successTitle?: string;
  errorTitle?: string;
  closeAria?: string;
};

export function ToastProvider({
  children,
  duration = 4500,
  labels,
}: {
  children: React.ReactNode;
  /** ms until auto-hide */
  duration?: number;
  labels?: ToastProviderLabels;
}) {
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const idRef = React.useRef(0);

  const push = React.useCallback((tone: ToastTone, message: string) => {
    const id = ++idRef.current;
    setItems((prev) => [...prev, { id, message, tone }]);
  }, []);

  const success = React.useCallback(
    (message: string) => push("success", message),
    [push],
  );
  const error = React.useCallback(
    (message: string) => push("error", message),
    [push],
  );

  const remove = React.useCallback((id: number) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const value = React.useMemo(() => ({ success, error }), [success, error]);

  return (
    <ToastContext.Provider value={value}>
      <ToastViewport
        duration={duration}
        items={items}
        remove={remove}
        labels={labels}
      >
        {children}
      </ToastViewport>
    </ToastContext.Provider>
  );
}

function ToastViewport({
  children,
  duration,
  items,
  remove,
  labels,
}: {
  children: React.ReactNode;
  duration: number;
  items: ToastItem[];
  remove: (id: number) => void;
  labels?: ToastProviderLabels;
}) {
  const regionLabel = labels?.regionLabel ?? "Notifications";
  const successTitle = labels?.successTitle ?? "Success";
  const errorTitle = labels?.errorTitle ?? "Error";
  const closeAria = labels?.closeAria ?? "Close";

  return (
    <Toast.Provider
      duration={duration}
      swipeDirection="up"
      label={regionLabel}
    >
      {children}
      {items.map((item) => {
        const isError = item.tone === "error";
        return (
          <Toast.Root
            key={item.id}
            duration={duration}
            className={cn(
              "pointer-events-auto flex w-[min(380px,calc(100vw-32px))] items-start gap-3 rounded-lg border border-border-strong bg-surface p-4 shadow-raised",
              "data-[swipe=move]:translate-y-(--radix-toast-swipe-move-y) data-[swipe=cancel]:translate-y-0 data-[swipe=end]:translate-y-(--radix-toast-swipe-end-y)",
            )}
            onOpenChange={(open) => {
              if (!open) remove(item.id);
            }}
          >
            <div
              aria-hidden
              className={cn(
                "mt-0.5 h-8 w-1 shrink-0 rounded-full",
                isError
                  ? "bg-(--status-error)"
                  : "bg-(--status-active)",
              )}
            />
            <div className="min-w-0 flex-1">
              <Toast.Title
                className={cn(
                  "text-ui-dense font-semibold",
                  isError
                    ? "text-(--status-error)"
                    : "text-foreground",
                )}
              >
                {isError ? errorTitle : successTitle}
              </Toast.Title>
              <Toast.Description className="mt-1 text-ui-dense text-secondary">
                {item.message}
              </Toast.Description>
            </div>
            <Toast.Close
              type="button"
              className="shrink-0 rounded p-1 text-muted outline-none hover:bg-elevated hover:text-foreground"
              aria-label={closeAria}
            >
              <XIcon size={16} weight="bold" />
            </Toast.Close>
          </Toast.Root>
        );
      })}
      <Toast.Viewport className="fixed top-0 right-0 z-250 flex max-h-dvh w-[min(420px,100vw)] flex-col-reverse gap-2 p-4 pt-[max(1rem,env(safe-area-inset-top,0px))] outline-none" />
    </Toast.Provider>
  );
}

export function useToast(): ToastContextValue {
  const v = React.useContext(ToastContext);
  if (!v) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return v;
}
